import { Component, HostListener, OnDestroy, OnInit } from '@angular/core';
import { UserGroup } from '@models';
import { GlobalSearchType, HOME_SEARCH_STATE_KEY, HOME_SEARCH_STATE_VERSION, SavedHomeSearch, SearchResult, SearchResultGroup } from '@models/GlobalSearch/globalSearch.model';
import { GlobalSearchOutcome, GlobalSearchService } from '@services/globalSearch.service';
import { UserService } from '@services/user.service';
import { Subject, merge, of } from 'rxjs';
import { catchError, debounceTime, distinctUntilChanged, map, switchMap, takeUntil, tap } from 'rxjs/operators';
import { AppComponent } from '../../../app.component';

// One piece of a snippet; `match` pieces are shown in bold.
export interface SnippetPart {
  text: string;
  match: boolean;
}

// The home page search box and its results. Results show between the box and the tiles, so they push
// the tiles down.
@Component({
  standalone: false,
  selector: 'app-global-search',
  templateUrl: './global-search.component.html',
  styleUrls: ['./global-search.component.scss']
})
export class GlobalSearchComponent implements OnInit, OnDestroy {

  // Wait for the user to stop typing for this long before searching.
  private static readonly DEBOUNCE_MS = 500;
  private static readonly INITIAL_RESULTS_PER_GROUP = 5;

  searchText = '';
  searching = false;
  searched = false;
  // True while a remembered search is being run again in the background; its saved results stay on
  // screen until the fresh ones arrive.
  refreshing = false;
  outcome: GlobalSearchOutcome = { groups: [], total: 0, incomplete: false, pending: false };
  // The text the results on screen were found for. The box can already hold newer text (an answer to an
  // earlier search can arrive while the user is typing), so what is remembered and highlighted is this.
  private outcomeText = '';

  // Groups the user has expanded with "Show more"
  private expandedGroups = new Set<GlobalSearchType>();

  private username = '';
  private userGroups: UserGroup[] = [];
  private readonly globalSwitch: boolean;

  // Kept as the page scrolls: reading the scroll position while the page is being torn down is
  // unreliable, so the position to remember is the last one seen.
  private lastScrollY = 0;
  // Set once the user types, so a remembered search is not run again over what they are doing.
  private typedByUser = false;

  private typed$ = new Subject<string>();
  private refresh$ = new Subject<string>();
  private destroy$ = new Subject<void>();


  constructor(private searchService: GlobalSearchService,
              private userService: UserService,
              global: AppComponent) {
    this.globalSwitch = global.globalSwitch;
  }


  ngOnInit(): void {
    // Set up before anything below can ask for a search: a Subject drops what it is sent before it has
    // a subscriber, and the user lookups may answer straight away when they are already cached.
    merge(
      // What the user types, once they pause
      this.typed$.pipe(
        debounceTime(GlobalSearchComponent.DEBOUNCE_MS),
        distinctUntilChanged(),
        map(text => ({ text, refresh: false }))),
      // A remembered search run again as soon as it can be, with no pause
      this.refresh$.pipe(map(text => ({ text, refresh: true })))
    ).pipe(
      tap(({ text, refresh }) => {
        if (refresh) {
          // The remembered results stay on screen meanwhile
          this.refreshing = true;
          return;
        }
        this.refreshing = false;
        this.expandedGroups.clear();
        this.searching = text.trim().length >= GlobalSearchService.MIN_TEXT_LENGTH;
      }),
      // A newer search cancels the one still running, including its server request, so an old answer can
      // never replace newer results. Each search reports twice: what the browser found right away, then
      // everything once the server has answered (outcome.pending says which).
      switchMap(({ text }) => this.searchService.search(text, this.username, this.userGroups, this.globalSwitch).pipe(
        catchError(() => of({ groups: [], total: 0, incomplete: true, pending: false } as GlobalSearchOutcome)),
        tap(() => this.searched = text.trim().length >= GlobalSearchService.MIN_TEXT_LENGTH),
        map(outcome => ({ text, outcome })))),
      takeUntil(this.destroy$)
    ).subscribe(({ text, outcome }) => {
      if (this.refreshing) {
        // Keep the remembered results until the fresh ones are complete: the browser's part alone
        // would drop whatever the server had found.
        if (outcome.pending) return;
        this.refreshing = false;
        if (outcome.incomplete) {
          // The server could not be reached: the remembered results are still the better answer
          this.outcome = { ...this.outcome, incomplete: true, pending: false };
          return;
        }
      }

      this.outcome = outcome;
      this.outcomeText = text;
      this.searching = false;
      if (!outcome.pending) this.saveState();
    });

    // The access rules need the signed-in user and every user group. User groups are the only thing
    // the search loads itself; the rest it reads from what pages have already loaded.
    this.userService.getCurrentUserEmailUsingAzureAD().subscribe(
      results => {
        this.username = results?.['value'] ?? '';
        const restored = this.restoreState();

        this.userService.getUserGroups().subscribe(
          groups => {
            this.userGroups = groups ?? [];
            // Needs the user groups to filter by access, so it waits for them; skipped if the user
            // has already started a search of their own.
            if (restored && !this.typedByUser) this.refresh$.next(this.searchText);
          },
          () => this.userGroups = []);
      },
      () => this.username = '');
  }


  ngOnDestroy(): void {
    // Every way of leaving the page ends here (a result, the menu, Back, the logo)
    this.saveState();
    this.destroy$.next();
    this.destroy$.complete();
  }


  @HostListener('window:scroll')
  onWindowScroll(): void {
    this.lastScrollY = window.scrollY;
  }


  onTextChanged(): void {
    this.typedByUser = true;
    if (this.searchText.trim().length < GlobalSearchService.MIN_TEXT_LENGTH) {
      // Too short to search: drop old results right away rather than after the pause
      this.outcome = { groups: [], total: 0, incomplete: false, pending: false };
      this.outcomeText = '';
      this.searched = false;
      this.searching = false;
      this.refreshing = false;
      // An emptied box must not come back with the old results
      this.clearState();
    }
    this.typed$.next(this.searchText);
  }


  clear(): void {
    this.searchText = '';
    this.onTextChanged();
  }


  get tooShort(): boolean {
    const length = this.searchText.trim().length;
    return length > 0 && length < GlobalSearchService.MIN_TEXT_LENGTH;
  }


  visibleResults(group: SearchResultGroup): SearchResult[] {
    return this.expandedGroups.has(group.type)
      ? group.results
      : group.results.slice(0, GlobalSearchComponent.INITIAL_RESULTS_PER_GROUP);
  }


  hiddenCount(group: SearchResultGroup): number {
    return this.expandedGroups.has(group.type) ? 0 : Math.max(0, group.results.length - GlobalSearchComponent.INITIAL_RESULTS_PER_GROUP);
  }


  showMore(group: SearchResultGroup): void {
    this.expandedGroups.add(group.type);
    this.saveState();
  }


  // ---- Remembering the last search for this browser tab ----
  //
  // Following a result leaves the home page, so the search would otherwise be gone on the way back.
  // sessionStorage keeps it for the tab only; every access is guarded because storage can be blocked
  // or full, and the page works the same without it.

  // Called as a result is followed, before the page goes away
  onResultClick(): void {
    this.saveState();
  }


  private saveState(): void {
    if (!this.searched || this.outcomeText.trim().length < GlobalSearchService.MIN_TEXT_LENGTH || !this.username) return;

    const state: SavedHomeSearch = {
      version: HOME_SEARCH_STATE_VERSION,
      username: this.username,
      text: this.outcomeText,
      outcome: { groups: this.outcome.groups, total: this.outcome.total, incomplete: this.outcome.incomplete },
      expanded: Array.from(this.expandedGroups),
      scrollY: this.lastScrollY,
      savedAt: Date.now()
    };

    try {
      sessionStorage.setItem(HOME_SEARCH_STATE_KEY, JSON.stringify(state));
    } catch {
      // storage full or blocked: the search just is not remembered
    }
  }


  private clearState(): void {
    try {
      sessionStorage.removeItem(HOME_SEARCH_STATE_KEY);
    } catch {
      // nothing to clear if storage is unavailable
    }
  }


  // Show the remembered search at once, without the pause a typed search waits for. Returns true when
  // there was one to show. The caller then runs the search again to bring it up to date.
  private restoreState(): boolean {
    const saved = this.readState();
    if (saved == null) return false;

    // The results were filtered for whoever searched, so another user must not see them
    if (!this.username || saved.username.toLowerCase() !== this.username.toLowerCase()) {
      this.clearState();
      return false;
    }

    this.searchText = saved.text;
    this.outcomeText = saved.text;
    this.outcome = { groups: saved.outcome.groups, total: saved.outcome.total, incomplete: saved.outcome.incomplete, pending: false };
    this.expandedGroups = new Set(saved.expanded);
    this.searched = true;
    this.lastScrollY = saved.scrollY;

    // Once the results have rendered. Only if the user has not scrolled yet, and once: the refresh that
    // follows can change how many rows there are, and the page must not jump again when it does.
    if (saved.scrollY > 0) {
      setTimeout(() => {
        if (window.scrollY === 0) window.scrollTo({ top: saved.scrollY });
      });
    }
    return true;
  }


  // The remembered search, or null if there is none or it cannot be trusted (another version, damaged,
  // edited by hand). Anything unusable is removed.
  private readState(): SavedHomeSearch | null {
    try {
      const raw = sessionStorage.getItem(HOME_SEARCH_STATE_KEY);
      if (raw == undefined || raw === '') return null;

      const saved = JSON.parse(raw) as SavedHomeSearch;
      const usable = saved != undefined
        && saved.version === HOME_SEARCH_STATE_VERSION
        && typeof saved.username === 'string'
        && typeof saved.text === 'string'
        && saved.text.trim().length >= GlobalSearchService.MIN_TEXT_LENGTH
        && saved.outcome != undefined
        && Array.isArray(saved.outcome.groups)
        && saved.outcome.groups.every(g => g != undefined && Array.isArray(g.results))
        && Array.isArray(saved.expanded)
        && typeof saved.scrollY === 'number';

      if (!usable) {
        this.clearState();
        return null;
      }
      return saved;
    } catch {
      this.clearState();
      return null;
    }
  }


  // The matched text split into plain and matching pieces, so the match can be bold without ever
  // putting search results into innerHTML.
  snippetParts(result: SearchResult): SnippetPart[] {
    const text = result.matchedText ?? '';
    const needle = (this.outcomeText || this.searchText).trim().toLowerCase();
    if (needle === '') return [{ text, match: false }];

    // Long text (e.g. a description) is trimmed to the part around the match.
    const start = text.toLowerCase().indexOf(needle);
    let from = 0;
    let to = text.length;
    if (text.length > 160 && start >= 0) {
      from = Math.max(0, start - 60);
      to = Math.min(text.length, start + needle.length + 90);
    }

    const parts: SnippetPart[] = [];
    if (from > 0) parts.push({ text: '… ', match: false });

    const visible = text.substring(from, to);
    const lower = visible.toLowerCase();
    let index = 0;
    while (index < visible.length) {
      const hit = lower.indexOf(needle, index);
      if (hit < 0) {
        parts.push({ text: visible.substring(index), match: false });
        break;
      }
      if (hit > index) parts.push({ text: visible.substring(index, hit), match: false });
      parts.push({ text: visible.substring(hit, hit + needle.length), match: true });
      index = hit + needle.length;
    }

    if (to < text.length) parts.push({ text: ' …', match: false });
    return parts;
  }


  trackByType(_: number, group: SearchResultGroup): string {
    return group.type;
  }


  trackByResult(_: number, result: SearchResult): string {
    return result.type + ':' + result.id;
  }
}
